import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Modal,
  Typography,
  Spin,
  Alert,
  Tag,
  Button,
  Descriptions,
  Table,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  Row,
  Col,
} from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { GET_INSURANCE_CLAIM_BY_ID } from '../../graphql/queries';
import { UPDATE_INSURANCE_CLAIM } from '../../graphql/mutations';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ClaimDetailDialogProps {
  visible: boolean;
  claimId: string | null;
  onClose: () => void;
}

const ClaimDetailDialog: React.FC<ClaimDetailDialogProps> = ({
  visible,
  claimId,
  onClose,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [claimForm] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [claimItems, setClaimItems] = useState<any[]>([]);

  const [getClaimById, { data: claimData, loading: claimLoading, error: claimError, refetch }] = useLazyQuery(
    GET_INSURANCE_CLAIM_BY_ID
  );

  const [updateClaim, { loading: updating }] = useMutation(UPDATE_INSURANCE_CLAIM);

  useEffect(() => {
    if (visible && claimId) {
      getClaimById({ variables: { id: claimId } });
    } else if (!visible) {
      // Reset edit mode when dialog closes
      setIsEditMode(false);
      setEditingItemId(null);
      claimForm.resetFields();
      itemForm.resetFields();
      setClaimItems([]);
    }
  }, [visible, claimId, getClaimById, claimForm, itemForm]);

  useEffect(() => {
    if (claimData?.insuranceClaimResult) {
      const claim = claimData.insuranceClaimResult;
      setClaimItems(claim.items || []);
      claimForm.setFieldsValue({
        status: claim.status,
        isActive: claim.isActive,
        approvedAmount: claim.claimResultDetails?.approved_amount,
        rejectedAmount: claim.claimResultDetails?.rejected_amount,
        rejectedReason: claim.claimResultDetails?.rejected_reason,
      });
    }
  }, [claimData, claimForm]);

  const claim = claimData?.insuranceClaimResult;

  const handleSaveClaim = async () => {
    try {
      const values = await claimForm.validateFields();
      const updateDto: any = {
        status: values.status,
        isActive: values.isActive,
        claimResultDetails: {
          approved_amount: values.approvedAmount || 0,
          rejected_amount: values.rejectedAmount || 0,
          rejected_reason: values.rejectedReason || null,
        },
        items: claimItems.map(item => ({
          name: item.name,
          category: item.category,
          amount: String(item.amount),
          approvedAmount: String(item.approvedAmount),
          transactionId: item.transactionId || undefined,
        })),
      };

      await updateClaim({
        variables: {
          id: claimId,
          updateInsuranceClaimResultDto: updateDto,
        },
      });

      message.success('Claim updated successfully');
      setIsEditMode(false);
      refetch();
    } catch (error: any) {
      message.error(error?.message || 'Failed to update claim');
    }
  };

  const handleAddItem = () => {
    setEditingItemId('new');
    itemForm.resetFields();
  };

  const handleEditItem = (item: any) => {
    setEditingItemId(item.id);
    itemForm.setFieldsValue({
      name: item.name,
      category: item.category,
      amount: parseFloat(item.amount),
      approvedAmount: parseFloat(item.approvedAmount),
      transactionId: item.transactionId,
    });
  };

  const handleSaveItem = async () => {
    try {
      const values = await itemForm.validateFields();
      const newItem = {
        id: editingItemId === 'new' ? `temp-${Date.now()}` : editingItemId,
        name: values.name,
        category: values.category,
        amount: values.amount,
        approvedAmount: values.approvedAmount,
        transactionId: values.transactionId,
        createdAt: editingItemId === 'new' ? new Date().toISOString() : claimItems.find(i => i.id === editingItemId)?.createdAt,
        updatedAt: new Date().toISOString(),
      };

      if (editingItemId === 'new') {
        setClaimItems([...claimItems, newItem]);
      } else {
        setClaimItems(claimItems.map(item => (item.id === editingItemId ? newItem : item)));
      }

      setEditingItemId(null);
      itemForm.resetFields();
    } catch (error) {
      // Validation error, do nothing
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setClaimItems(claimItems.filter(item => item.id !== itemId));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingItemId(null);
    itemForm.resetFields();
    if (claim) {
      claimForm.setFieldsValue({
        status: claim.status,
        isActive: claim.isActive,
        approvedAmount: claim.claimResultDetails?.approved_amount,
        rejectedAmount: claim.claimResultDetails?.rejected_amount,
        rejectedReason: claim.claimResultDetails?.rejected_reason,
      });
      setClaimItems(claim.items || []);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Claim Details
          </div>
          {!isEditMode && claim && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setIsEditMode(true)}
            >
              Edit
            </Button>
          )}
        </div>
      }
      open={visible}
      onCancel={isEditMode ? handleCancelEdit : onClose}
      footer={[
        isEditMode ? (
          <Space key="edit-actions">
            <Button onClick={handleCancelEdit}>Cancel</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveClaim}
              loading={updating}
            >
              Save Changes
            </Button>
          </Space>
        ) : (
          <Button key="close" onClick={onClose}>
            Close
          </Button>
        ),
      ]}
      width={1000}
      destroyOnClose
    >
      {claimLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : claimError ? (
        <Alert
          message="Error"
          description={claimError.message}
          type="error"
          showIcon
        />
      ) : claim ? (
        <div style={{ padding: '16px 0' }}>
          {isEditMode ? (
            <Form form={claimForm} layout="vertical">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Claim ID">
                  <Text code>{claim.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Policy ID">
                  <Text code>{claim.policyId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Appointment ID">
                  <Text code>{claim.appointmentId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Form.Item name="status" rules={[{ required: true }]}>
                    <Select>
                      <Select.Option value="pending">Pending</Select.Option>
                      <Select.Option value="approved">Approved</Select.Option>
                      <Select.Option value="partially_approved">Partially Approved</Select.Option>
                      <Select.Option value="rejected">Rejected</Select.Option>
                    </Select>
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Approved Amount">
                  <Form.Item name="approvedAmount">
                    <InputNumber
                      prefix="$"
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                    />
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Rejected Amount">
                  <Form.Item name="rejectedAmount">
                    <InputNumber
                      prefix="$"
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                    />
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Rejected Reason">
                  <Form.Item name="rejectedReason">
                    <TextArea rows={3} placeholder="Enter rejection reason if applicable" />
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Active">
                  <Form.Item name="isActive">
                    <Select>
                      <Select.Option value={true}>Yes</Select.Option>
                      <Select.Option value={false}>No</Select.Option>
                    </Select>
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {dayjs(claim.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                  {dayjs(claim.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              </Descriptions>
            </Form>
          ) : (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Claim ID">
                <Text code>{claim.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Policy ID">
                <Text code>{claim.policyId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Appointment ID">
                <Text code>{claim.appointmentId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    claim.status?.toLowerCase() === 'approved'
                      ? 'green'
                      : claim.status?.toLowerCase() === 'rejected'
                      ? 'red'
                      : 'orange'
                  }
                >
                  {claim.status}
                </Tag>
              </Descriptions.Item>
              {claim.claimResultDetails && (
                <>
                  {claim.claimResultDetails.approved_amount !== undefined &&
                    claim.claimResultDetails.approved_amount !== null && (
                      <Descriptions.Item label="Approved Amount">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          ${typeof claim.claimResultDetails.approved_amount === 'number'
                            ? claim.claimResultDetails.approved_amount.toFixed(2)
                            : parseFloat(String(claim.claimResultDetails.approved_amount)).toFixed(2)}
                        </Text>
                      </Descriptions.Item>
                    )}
                  {claim.claimResultDetails.rejected_amount !== undefined &&
                    claim.claimResultDetails.rejected_amount !== null && (
                      <Descriptions.Item label="Rejected Amount">
                        <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                          ${typeof claim.claimResultDetails.rejected_amount === 'number'
                            ? claim.claimResultDetails.rejected_amount.toFixed(2)
                            : parseFloat(String(claim.claimResultDetails.rejected_amount)).toFixed(2)}
                        </Text>
                      </Descriptions.Item>
                    )}
                  {claim.claimResultDetails.rejected_reason && (
                    <Descriptions.Item label="Rejected Reason">
                      <Text>{claim.claimResultDetails.rejected_reason}</Text>
                    </Descriptions.Item>
                  )}
                </>
              )}
              <Descriptions.Item label="Active">
                <Tag color={claim.isActive ? 'green' : 'red'}>
                  {claim.isActive ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(claim.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {dayjs(claim.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Divider orientation="left">
            <Space>
              Claim Items
              {isEditMode && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddItem}
                >
                  Add Item
                </Button>
              )}
            </Space>
          </Divider>

          {editingItemId && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
              <Form form={itemForm} layout="vertical">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="name"
                      label="Name"
                      rules={[{ required: true, message: 'Please enter item name' }]}
                    >
                      <Input placeholder="Item name" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: 'Please enter category' }]}
                    >
                      <Input placeholder="Category" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="amount"
                      label="Amount"
                      rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                      <InputNumber
                        prefix="$"
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        placeholder="Amount"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="approvedAmount"
                      label="Approved Amount"
                      rules={[{ required: true, message: 'Please enter approved amount' }]}
                    >
                      <InputNumber
                        prefix="$"
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        placeholder="Approved amount"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="transactionId" label="Transaction ID">
                      <Input placeholder="Transaction ID (optional)" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Space style={{ marginTop: 30 }}>
                      <Button type="primary" onClick={handleSaveItem}>
                        Save
                      </Button>
                      <Button onClick={() => {
                        setEditingItemId(null);
                        itemForm.resetFields();
                      }}>
                        Cancel
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            </div>
          )}

          <Table
            dataSource={claimItems}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Category',
                dataIndex: 'category',
                key: 'category',
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                render: (amount: any) => {
                  if (amount == null || amount === '') return '-';
                  const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
                  return isNaN(numAmount) ? '-' : `$${numAmount.toFixed(2)}`;
                },
              },
              {
                title: 'Approved Amount',
                dataIndex: 'approvedAmount',
                key: 'approvedAmount',
                render: (amount: any) => {
                  if (amount == null || amount === '') return '-';
                  const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
                  return isNaN(numAmount) ? '-' : `$${numAmount.toFixed(2)}`;
                },
              },
              {
                title: 'Transaction ID',
                dataIndex: 'transactionId',
                key: 'transactionId',
                render: (id: string) => (id ? <Text code>{id.substring(0, 8)}...</Text> : '-'),
              },
              {
                title: 'Created At',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
              },
              ...(isEditMode
                ? [
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, record: any) => (
                        <Space>
                          <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEditItem(record)}
                          >
                            Edit
                          </Button>
                          <Popconfirm
                            title="Are you sure you want to delete this item?"
                            onConfirm={() => handleDeleteItem(record.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No claim items' }}
          />
        </div>
      ) : (
        <Alert
          message="No claim data available"
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default ClaimDetailDialog;

